
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from '@vue/composition-api';
import Tooltip from '05-ui-kit/lib/Tooltip';
import { useContext, useRoute, useRouter } from '@nuxtjs/composition-api';
import type { EmitteryType } from '~/plugins/emitter';
import { useTokenStore } from '~/store/token';
import { useUserStore } from '~/store/user';
import { useAuthStore } from '~/store/modal/auth';
import type { Product } from '~/type/product/Product';
import { useLocationStore } from '~/store/location';
import type { LoanErrorDetails } from '~/index';
const __sfc_main = {};
__sfc_main.props = {
  product: {
    key: "product",
    required: false,
    type: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emitter = inject<EmitteryType>('emitter')!;
  const tokenStore = useTokenStore();
  const {
    $config,
    $sentry
  } = useContext();
  const route = useRoute();
  const router = useRouter();
  const loading = ref(false);
  const userStore = useUserStore();
  const userPhone = computed(() => userStore.user?.phone);
  const locationStore = useLocationStore();
  const isUser = computed(() => !!userStore.isUser);
  const authStore = useAuthStore();
  watch(() => tokenStore.accessToken, () => {
    removeLoanWidget();
  });
  onUnmounted(() => {
    removeLoanWidget();
  });
  onMounted(() => {
    if (route.value.query.bnpl) nextTick(() => enableLoanDetailModal());
  });
  async function injectLoanWidget(cb: () => void) {
    loading.value = true;
    const script = (document.querySelector('#widget-script') as HTMLScriptElement);
    const style = (document.querySelector('#widget-css') as HTMLLinkElement);
    if (script && style) {
      loading.value = false;
      cb();
    } else {
      if (!style) {
        const loanStyle = document.createElement('link');
        loanStyle.href = `${$config.loanWidgetUrl}/style.css`;
        loanStyle.rel = 'stylesheet';
        loanStyle.setAttribute('id', 'widget-css');
        document.head.appendChild(loanStyle);
      }
      if (!script) {
        const loanScript = document.createElement('script');
        loanScript.src = `${$config.loanWidgetUrl}/loan-widget.js`;
        loanScript.async = true;
        loanScript.setAttribute('id', 'widget-script');
        loanScript.onload = () => {
          loading.value = false;
          cb();
          initEvents();
        };
        loanScript.onerror = () => {
          loading.value = false;
          Tooltip({
            type: 'error',
            title: 'Загрузка прервана',
            description: 'Что-то пошло не так. Пожалуйста, проверьте интернет-соединение и попробуйте снова. VPN может мешать работе и вызывать ошибки.'
          });
          const error = {
            native: {
              message: 'Произошла ошибка инициализации виджета'
            }
          };
          emitter.emit('loan-request-error', {
            error
          });
          sendErrorToSentry(error);
        };
        document.head.appendChild(loanScript);
      }
    }
  }
  function removeLoanWidget() {
    const script = (document.querySelector('#widget-script') as HTMLScriptElement);
    const style = (document.querySelector('#widget-css') as HTMLLinkElement);
    const loanWidget = (document.querySelector('#loan-widget') as HTMLDivElement);
    if (script) script.remove();
    if (style) style.remove();
    if (loanWidget) loanWidget.remove();
    removeQueryParams();
  }
  function removeQueryParams() {
    if (route.value.query.bnpl) {
      const query = {
        ...route.value.query
      };
      delete query.bnpl;
      router.replace({
        query
      });
    }
  }
  function onRequest(cb: () => void, option: {
    type: 'lk' | 'form';
  } = {
    type: 'lk'
  }) {
    if (option.type === 'form') emitter.emit('loan-by-installment');
    if (isUser.value) {
      cb();
    } else {
      authStore.enable({
        subtitle: option.type === 'lk' ? 'Пожалуйста, введите ваш номер телефона, чтобы получить код для авторизации и войти в личный кабинет рассрочки.' : 'Пожалуйста, введите ваш номер телефона, чтобы получить код для авторизации и продолжить оформление рассрочки.',
        onComplete: () => nextTick(() => cb())
      });
    }
  }
  function isActiveWidget() {
    return !!document.getElementById('loan-widget');
  }
  function enableLoanDetailModal() {
    onRequest(() => injectLoanWidget(() => {
      if (!isActiveWidget()) {
        window.init({
          token: tokenStore.accessToken!.value!,
          phone: userPhone.value!.slice(1)
        });
      }
      window.loanWidget.enableLoanDetailModal();
    }));
  }
  function enableCalculation() {
    onRequest(() => injectLoanWidget(() => {
      if (!isActiveWidget()) {
        window.init({
          token: tokenStore.accessToken!.value!,
          phone: userPhone.value!.slice(1)
        });
      }
      if (props.product) window.loanWidget.enableCalculationModal(props.product);
    }), {
      type: 'form'
    });
  }
  function sendErrorToSentry(error: LoanErrorDetails) {
    $sentry.setContext('User Location', {
      currentRegionName: locationStore?.regionName,
      currentRegionId: locationStore?.regionID,
      currentCityName: locationStore?.cityName,
      currentCityId: locationStore?.сityID
    });
    const {
      name,
      login,
      email,
      id
    } = {
      ...userStore.user
    };
    $sentry.setUser({
      name: error?.userData?.fullname || name,
      login: error?.userData?.phone || login,
      email: error?.userData?.email || email,
      id: id?.toString(),
      ip_address: userStore.ip
    });
    $sentry.setContext('Error description', error);
    $sentry.captureException(`[LOAN] - ${error?.native?.message || 'Произошла ошибка запроса'}`);
  }
  function initEvents() {
    window.loanWidget?.emitter.on('enable-loan-detail', ({
      value
    }: {
      value: boolean;
    }) => {
      emitter.emit('loan-open-user', {
        isAuth: value
      });
    });
    window.loanWidget?.emitter.on('enable-how-to-pay', () => {
      emitter.emit('loan-to-pay');
    });
    window.loanWidget?.emitter.on('enable-detail-entity', ({
      value
    }: {
      value: boolean;
    }) => {
      emitter.emit('loan-open-details', {
        paymentOverdue: value
      });
    });
    window.loanWidget?.emitter.on('available-express', () => {
      emitter.emit('loan-available-express');
    });
    window.loanWidget?.emitter.on('issued-express', () => {
      emitter.emit('loan-success-express');
    });
    window.loanWidget?.emitter.on('timeout-error', () => {
      emitter.emit('loan-timeout-error');
      $sentry.captureException('[LOAN] - Запрос превысил лимит времени ожидания.');
    });
    window.loanWidget?.emitter.on('request-error', ({
      error
    }) => {
      emitter.emit('loan-request-error', {
        error
      });
      sendErrorToSentry(error);
    });

    /**
     * Удаляем параметр для инита сервиса, после того как закрыли окно
     */
    window.loanWidget.emitter.on('close-widget', () => {
      removeLoanWidget();
    });
  }
  return {
    loading,
    enableLoanDetailModal,
    enableCalculation
  };
};
export default __sfc_main;
